/**
 * On-screen battle controls for touch screens: semi-transparent round buttons in the game's colours, shown only while
 * the local player controls a worm, and only the ones that do something in the game's current state (the game's
 * battleControls, src/game/runtime/mobile-port.ts): the sight's arrows for a weapon that aims, the fuse and the bounce
 * for a weapon that has them, the confirmation of a target, the steering of a jet pack, and so on.
 *
 * Every button is a key of the keyboard or a click of the mouse and goes through the same injection as the physical
 * key or the finger on the map (FlashPlayer.injectKey / injectMouse): the game cannot tell them apart, so the battle's
 * recording, online play and replays are the same as with a keyboard and a mouse, and no gameplay rule changes.
 *  - Arrows walk (left / right) and aim (up / down); in a strike's target mode left / right choose the side the strike
 *    comes from, for a girder they turn it, in the worm choice right is the next worm (Enter); a jet pack and a
 *    parachute are steered by all four.
 *  - The forward jump is Enter, the back-flip Backspace; their icons point where the worm will go (it faces left or
 *    right), the forward jump low and long, the back-flip high and backwards, as the game's help describes them.
 *  - Fire is Space, held to charge the power of the weapons that have one and released to shoot; it also stops a jet
 *    pack, a parachute, a blow torch or a drill, and explodes a sheep on its way. A weapon that waits for a point of the
 *    map (the strikes, the teleports, the girders) is aimed by a tap on the map, which moves the game's own target
 *    cursor there, and the fire button confirms it: the double click of the mouse at the cursor, as the original is
 *    played (a double tap on the map works too). A hint says what the mode wants.
 *  - The weapon button is Q (the weapon sheet); in a target mode it is Escape (leaves the mode, which the weapon sheet
 *    cannot open in); while the sheet or the scanner is open it closes them by their own close buttons (the back
 *    action). In the worm choice the fire button is Escape (this worm).
 *  - The fuse button sends the next of the digit keys 1-5 and shows the game's current fuse; the bounce button sends
 *    '+' or '-' and shows the game's current bounce.
 * A press lasts at least MIN_PRESS_MS so that the game's per-frame key poll sees even the shortest tap. Several fingers
 * work at once (walk and aim, charge and aim). A press is shown (the button lights up and shrinks) and, where the
 * platform vibrates, felt.
 *
 * Placement (placeControls, the one function that decides where the buttons sit; layout() applies it): beside the
 * 807x600 window when the screen leaves room there (a phone in landscape: over the wide view's margin,
 * src/shared/wide-view.ts, or the letterbox bars), or under it (portrait), so nothing of the window is covered;
 * otherwise over its lower corners, more transparent (a tablet). The wide screen's battle HUD keeps clear of the
 * clusters (clusterRects: the d-pad, the action buttons and the bounce button's column above them;
 * src/game/ui/wide-screen.ts); a HUD icon still under a cluster (`keepClear`: no wide view)
 * moves the cluster towards the middle so it stays tappable. In the thumbs' reach at the bottom,
 * buttons at least MIN_BUTTON_PX, at least MIN_GAP_PX apart, inside the safe area (notches, rounded corners, system
 * bars: `safeInsets`). The d-pad is an inverted T: ◀ ▼ ▶ in one row along the bottom edge (where the thumb rests to
 * walk, level with the action cluster's bottom), ▲ above ▼.
 */

/** Keys of the battle (Windows virtual key codes, as the keyboard sends them). */
export const KEY = { left: 37, up: 38, right: 39, down: 40, enter: 13, backspace: 8, space: 32, q: 81, escape: 27, plus: 187, minus: 189 } as const;

/** Smallest button (CSS px): the 48 dp touch target of the Android guidelines (above the 44 px web minimum). */
export const MIN_BUTTON_PX = 48;

/** Smallest space between two buttons (CSS px): the 8 dp of the Android guidelines. */
export const MIN_GAP_PX = 8;

/** Narrowest the menus' info gets (CSS px): the longest word of its hint on a line. */
export const INFO_MIN_PX = 100;

/**
 * The shorter side of a screen from which it is a tablet (CSS px): there the buttons over the window's corners are
 * more transparent (they cover the game's own window); on a phone they lie over the battle's map like the wide view's
 * margin, opaque as beside the window.
 */
export const TABLET_MIN_PX = 600;

/** Shortest press sent to the game (ms): longer than one frame of its 50 FPS key poll. */
export const MIN_PRESS_MS = 60;

/**
 * Longest a key stays down after the finger lifted while the game has not taken it yet (ms): the weapon sheet opens
 * only while the worm stands idle (proc.open_wpsheet), so a tap during a landing or the weapon's animation waits.
 */
export const STICKY_PRESS_MS = 1200;

/** How long a press vibrates where the platform can (ms). */
export const HAPTIC_MS = 12;

/**
 * What the controls offer now: the game's battleControls (src/game/runtime/mobile-port.ts BattleControls; only the
 * fields the controls read).
 */
export interface ControlsState {
  mode: 'turn' | 'retreat' | 'target' | 'worm' | 'fly' | 'burst' | 'tool' | 'busy' | 'menu';
  sides: 'walk' | 'steer' | 'strike' | 'girder' | 'worm' | null;
  vertical: 'aim' | 'steer' | null;
  jumps: boolean;
  facing: 'left' | 'right';
  fire: 'charge' | 'shoot' | 'use' | 'stop' | 'detonate' | 'confirm' | null;
  target: 'strike' | 'teleport' | 'girder' | null;
  strikeFrom: 'left' | 'right' | null;
  cancel: boolean;
  fuse: number | null;
  bounce: 'max' | 'min' | null;
  weapons: boolean;
  clock: number | null;
  hint: string[];
  hintReady: string[];
}

export interface TouchControlsOptions {
  /** Sends a key event exactly like the keyboard does (FlashPlayer.injectKey). */
  key(type: 'down' | 'up', keyCode: number, charCode: number): void;
  /** What the controls offer now, or null: none (polled by update()). */
  state(): ControlsState | null;
  /** The stage's rectangle in CSS px (to place the buttons beside it). */
  stageRect(): { left: number; top: number; width: number; height: number } | null;
  /** Sends a mouse event at a stage point exactly like a finger on the stage does (FlashPlayer.injectMouse). */
  mouse?(type: 'down' | 'up', x: number, y: number): void;
  /**
   * The mouse: where the game's target cursor is (a tap on the map moves it there), as a stage point (x, y) and on the
   * page (clientX, clientY: a ring around the cursor, which is small on a phone).
   */
  pointer?(): { x: number; y: number; clientX: number; clientY: number } | null;
  /** Whether a finger at this client point touches the battle's map (not its interface over it). */
  onMap?(clientX: number, clientY: number): boolean;
  /**
   * The client y below the battle HUD at the top's middle (the team bars): where the hint goes when it would cover the
   * chosen target at the bottom; null: none (the stage's top then).
   */
  hintTop?(): number | null;
  /** The back action (the Android back button's): closes the weapon sheet or the scanner by its own close button. */
  back?(): void;
  /** The player's language for a source text of the game (its translate). */
  text?(source: string): string;
  /** A short vibration (haptic feedback of a press); absent where the platform has none. */
  vibrate?(ms: number): void;
  /** The screen's edges under notches, rounded corners or system bars (CSS px; default none). */
  safeInsets?(): SafeInsets;
  /** Rectangles on the page (CSS px) the buttons must not cover: the battle HUD's icons over the window. */
  keepClear?(): CssRect[];
  /**
   * Rectangles on the page (CSS px) of an open menu's content the menus' info keeps clear of where it can (the weapon
   * sheet's cells): it narrows, not below INFO_MIN_PX, to the room left of them.
   */
  menuKeepClear?(): CssRect[];
  /**
   * A place (CSS px) the open menu keeps for the menus' info (the weapon sheet's footer row on a phone: its touch
   * layout), where it goes on one line; null: beside the window or at the d-pad's corner.
   */
  menuInfoAt?(): (CssRect & { hidden?: boolean }) | null;
  doc?: Document;
}

/** Insets of the screen's edges (CSS px) the buttons stay clear of. */
export interface SafeInsets {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** A rectangle on the page (CSS px). */
export interface CssRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Where the two clusters go (CSS px): the d-pad at the left, the actions at the right, and their button size. */
export interface ControlsPlacement {
  mode: 'side' | 'bottom' | 'over';
  unit: number;
  gap: number;
  /** The clusters' top-left corners and sizes. */
  pad: { x: number; y: number };
  act: { x: number; y: number };
  /** The d-pad's width (three buttons) and the action cluster's height. */
  padSize: number;
  /** The d-pad's height: two rows (▲; ◀ ▼ ▶ along the bottom), its bottom level with the action cluster's. */
  padH: number;
  actW: number;
  /** How far the action cluster reaches above its top-left corner (its bounce button, above the fuse). */
  actTop: number;
}

/**
 * The clusters' rectangles (CSS px) of a placement, whether their buttons are shown or not (the game keeps its battle
 * HUD clear of them: src/game/ui/wide-screen.ts): the d-pad (its two rows), the action buttons, and the column of the
 * bounce button above the fuse. The room above the weapon button stays out of them: the HUD's surrender button goes
 * there.
 */
export function clusterRects(p: ControlsPlacement): CssRect[] {
  const small = p.actTop - p.gap;
  return [
    { left: p.pad.x, top: p.pad.y, width: p.padSize, height: p.padH },
    { left: p.act.x, top: p.act.y, width: p.actW, height: p.padSize },
    { left: p.act.x + p.actW - small * 2 - p.gap, top: p.act.y - p.actTop, width: small, height: p.actTop },
  ];
}

const NO_INSETS: SafeInsets = { left: 0, top: 0, right: 0, bottom: 0 };

/**
 * The placement of the controls on a `W` x `H` screen (CSS px) with the 807x600 window at `stage`: beside the window
 * when the room at both its sides fits the d-pad ('side': a phone in landscape, over the wide view's margin), else under
 * it ('bottom': portrait), else over its lower corners ('over': a tablet); low, where the thumbs are, inside the safe
 * area. The unit is 48-64 px, the largest that fits. A cluster that would cover a `keepClear` rectangle (the battle
 * HUD's icons) moves sideways, towards the screen's middle, until it no longer does.
 */
export function placeControls(W: number, H: number, stage: CssRect, safe: SafeInsets = NO_INSETS, keepClear: readonly CssRect[] = []): ControlsPlacement {
  const margin = 12;
  const gapOf = (u: number) => Math.max(MIN_GAP_PX, Math.round(u * 0.12));
  const padOf = (u: number) => u * 3 + gapOf(u) * 2;
  const x0 = safe.left;
  const x1 = W - safe.right;
  const y1 = H - safe.bottom;
  let unit = Math.round(Math.max(MIN_BUTTON_PX, Math.min(64, Math.min(x1 - x0, y1 - safe.top) * 0.14)));
  const leftBar = stage.left - x0;
  const rightBar = x1 - (stage.left + stage.width);
  const side = Math.min(leftBar, rightBar) - margin;
  const bottomBar = y1 - (stage.top + stage.height) - margin;
  let mode: ControlsPlacement['mode'] = 'over';
  if (side >= padOf(MIN_BUTTON_PX)) {
    mode = 'side';
    while (unit > MIN_BUTTON_PX && padOf(unit) > side) unit--;
  } else if (bottomBar >= padOf(MIN_BUTTON_PX)) {
    mode = 'bottom';
    while (unit > MIN_BUTTON_PX && padOf(unit) > bottomBar) unit--;
  }
  const gap = gapOf(unit);
  const padSize = padOf(unit);
  // The d-pad: ▲ over ◀ ▼ ▶, the row along the bottom (where the thumb walks), level with the action cluster's bottom.
  const padH = unit * 2 + gap;
  const actW = Math.round(unit * 1.35) + unit + gap * 2;
  // The action cluster's top reaches its bounce button, one small button (0.85 unit) above the d-pad's height.
  const actTop = Math.max(MIN_BUTTON_PX, Math.round(unit * 0.85)) + gap;
  const y = Math.round(y1 - padSize - margin);
  const padY = Math.round(y1 - padH - margin);
  let pad = { x: Math.round(x0 + margin), y: padY };
  let act = { x: Math.round(x1 - actW - margin), y };
  if (mode === 'side') {
    // Beside the window (a wide phone in landscape): the d-pad centred in the room at the left, the (narrower) action
    // cluster as far from the right edge as a d-pad centred there would be (the same outer margin at both sides, the
    // battle HUD in line with it), low where the thumbs are.
    pad = { x: Math.round(x0 + (leftBar - padSize) / 2), y: padY };
    act = { x: Math.round(x1 - (rightBar - padSize) / 2 - actW), y };
  }
  // Clear of the HUD: the cluster moves towards the middle past every rectangle it would cover.
  const hits = (x: number, w: number, top: number, h: number) => keepClear.filter((r) => x < r.left + r.width && x + w > r.left && top < r.top + r.height && top + h > r.top);
  // The action cluster: its buttons' block and the bounce button's column above the fuse (clusterRects).
  const small = actTop - gap;
  for (let n = 0; n < 8; n++) {
    const hit = [...hits(act.x, actW, act.y, padSize), ...hits(act.x + actW - small * 2 - gap, small, act.y - actTop, actTop)];
    if (!hit.length) break;
    act = { x: Math.round(Math.min(...hit.map((r) => r.left)) - margin - actW), y };
  }
  for (let n = 0; n < 8; n++) {
    const hit = hits(pad.x, padSize, pad.y, padH);
    if (!hit.length) break;
    pad = { x: Math.round(Math.max(...hit.map((r) => r.left + r.width)) + margin), y: padY };
  }
  return { mode, unit, gap, pad, act, padSize, padH, actW, actTop };
}

export type Slot = 'up' | 'left' | 'right' | 'down' | 'fire' | 'jump' | 'backflip' | 'weapon' | 'fuse' | 'bounce';

/** What a button does now: a key (held as long as the finger), or an action (on release over the button). */
interface Binding {
  /** What it does (data-act: tests, and the icon's cache key with `icon`). */
  act: string;
  icon: string;
  /** Key code and char code; for the fuse and the bounce computed at the press (the game's current value). */
  key?: () => [number, number];
  run?: () => void;
  /** A key kept down after the release until this holds (at most STICKY_PRESS_MS): the game took it. */
  until?: () => boolean;
  /** Shown but waiting (a target mode before a point of the map is chosen): a press only repeats the hint. */
  idle?: boolean;
  /** The chosen one of a pair (a strike's side). */
  sel?: boolean;
}

const CSS = `
#fwp-touch{position:fixed;inset:0;pointer-events:none;z-index:5;display:none;
  padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)}
#fwp-touch .cluster{position:absolute;pointer-events:none}
#fwp-touch button{position:absolute;pointer-events:auto;touch-action:none;user-select:none;-webkit-user-select:none;
  -webkit-tap-highlight-color:transparent;border-radius:50%;padding:0;margin:0;display:flex;align-items:center;justify-content:center;
  border:2px solid rgba(255,214,110,.8);background:rgba(28,38,12,.5);color:#ffe7a0;transition:transform 60ms,background-color 60ms;
  font:bold 22px Arial,sans-serif;line-height:1;text-shadow:0 1px 2px #000;box-shadow:0 2px 6px rgba(0,0,0,.5)}
#fwp-touch button svg{width:62%;height:62%;overflow:visible;filter:drop-shadow(0 1px 1.5px rgba(0,0,0,.8));pointer-events:none}
#fwp-touch button.on{background:rgba(110,150,30,.85);border-color:#fff2b0;transform:scale(.92)}
#fwp-touch button.sel{border-color:#fff6c8;box-shadow:0 0 0 3px rgba(255,220,90,.75),0 2px 6px rgba(0,0,0,.5)}
#fwp-touch button.idle{opacity:.6}
#fwp-touch.over button{opacity:.75}
#fwp-touch.over button.idle{opacity:.55}
#fwp-touch .hint{position:absolute;display:none;pointer-events:none;box-sizing:border-box;padding:6px 12px;border-radius:10px;
  background:rgba(0,0,0,.62);color:#fff3c4;font:bold 14px/1.3 Arial,sans-serif;text-align:center;text-shadow:0 1px 1px #000,0 0 3px #000;
  text-wrap:balance;width:max-content;transform:translate(-50%,-100%)}
#fwp-touch .hint.below,#fwp-touch .hint.top{transform:translate(-50%,0)}
#fwp-touch .hint.ready{background:rgba(0,0,0,.45)}
#fwp-touch .hint.small{font-size:13px;padding:5px 8px}
#fwp-touch .hint.small .ico{width:1.3em;height:1.3em}
#fwp-touch .hint .ico{display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;width:1.45em;height:1.45em;
  margin:-.2em .08em;vertical-align:middle;border-radius:50%;border:1.5px solid rgba(255,214,110,.85);background:rgba(28,38,12,.75)}
#fwp-touch .hint .ico svg{width:70%;height:70%;overflow:visible}
#fwp-touch .hint.nudge{animation:fwp-touch-nudge .5s}
@keyframes fwp-touch-nudge{0%,100%{background:rgba(0,0,0,.62)}30%{background:rgba(150,110,10,.95)}}
#fwp-touch .info{position:absolute;display:none;pointer-events:none;box-sizing:border-box;left:max(8px,env(safe-area-inset-left,0px));
  top:max(8px,env(safe-area-inset-top,0px));max-width:min(220px,40vw);padding:6px 10px;border-radius:10px;background:rgba(0,0,0,.72);
  color:#fff3c4;font:bold 13px/1.3 Arial,sans-serif;text-shadow:0 1px 1px #000}
#fwp-touch .info.corner{top:auto;background:rgba(0,0,0,.86)}
#fwp-touch .info.slot{max-width:none;align-items:center;gap:12px;white-space:nowrap;padding:3px 10px;transform:translateY(-50%);
  background:rgba(0,0,0,.6);font-size:13px}
#fwp-touch .info.slot .clock{font-size:17px}
#fwp-touch .info .clock{display:flex;align-items:center;gap:6px;font-size:20px}
#fwp-touch .info .clock svg{width:20px;height:20px}
#fwp-touch .info .clock.low{color:#ff8a70}
#fwp-touch .marker{position:absolute;display:none;pointer-events:none;width:44px;height:44px;margin:-22px 0 0 -22px;border-radius:50%;
  border:3px solid rgba(255,236,150,.95);box-shadow:0 0 0 2px rgba(0,0,0,.55),inset 0 0 0 2px rgba(0,0,0,.55);animation:fwp-touch-pulse 1.2s infinite}
@keyframes fwp-touch-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.18)}}
`;

// ---------------------------------------------------------------- icons (drawn, not font glyphs: every WebView has them)

const svg = (body: string, mirror = false): string =>
  `<svg viewBox="0 0 24 24" aria-hidden="true">${mirror ? `<g transform="translate(24 0) scale(-1 1)">${body}</g>` : body}</svg>`;
const FILL = 'fill="currentColor"';
const LINE = 'fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"';

function starPath(cx: number, cy: number, outer: number, inner: number, points: number): string {
  let d = '';
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 ? inner : outer;
    const a = (Math.PI * i) / points - Math.PI / 2;
    d += `${i ? 'L' : 'M'}${(cx + r * Math.cos(a)).toFixed(2)} ${(cy + r * Math.sin(a)).toFixed(2)}`;
  }
  return d + 'Z';
}

const ICON = {
  left: svg(`<path d="M16 4 6 12l10 8z" ${FILL}/>`),
  right: svg(`<path d="M8 4l10 8-10 8z" ${FILL}/>`),
  up: svg(`<path d="M4 16l8-10 8 10z" ${FILL}/>`),
  down: svg(`<path d="M4 8l8 10 8-10z" ${FILL}/>`),
  /** The forward jump of a worm that faces right: long and low (Enter: vx 2, vy -3). */
  jump: (mirror: boolean) =>
    svg(`<circle cx="3.2" cy="19.5" r="2.3" ${FILL}/><path d="M5.2 17.2Q12.5 6 19.2 15.6" ${LINE} stroke-width="2.4"/><path d="M21.3 20.2 16.4 16.7l4.7-3.4z" ${FILL}/>`, mirror),
  /** The back-flip of a worm that faces right: high and backwards (Backspace: vx -0.8, vy -5). */
  backflip: (mirror: boolean) =>
    svg(`<circle cx="18.5" cy="20" r="2.3" ${FILL}/><path d="M18.5 16.8C18.5 1.5 6 .5 6 13.5" ${LINE} stroke-width="2.4"/><path d="M6 19.8 2.9 13.4h6.2z" ${FILL}/>`, mirror),
  fire: svg(`<path d="${starPath(12, 12, 11, 4.6, 8)}" ${FILL}/>`),
  detonate: svg(`<path d="${starPath(12, 12, 11, 5.5, 11)}" ${FILL}/><circle cx="12" cy="12" r="3" fill="rgba(0,0,0,.55)"/>`),
  stop: svg(`<rect x="5.5" y="5.5" width="13" height="13" rx="2.5" ${FILL}/>`),
  target: svg(`<circle cx="12" cy="12" r="7.2" ${LINE} stroke-width="2.2"/><path d="M12 1.5v5M12 17.5v5M1.5 12h5M17.5 12h5" ${LINE} stroke-width="2.2"/><circle cx="12" cy="12" r="2.2" ${FILL}/>`),
  check: svg(`<path d="M3.5 12.5 9.5 18.5 20.5 5.5" ${LINE} stroke-width="3.2"/>`),
  cancel: svg(`<path d="M5.5 5.5l13 13M18.5 5.5l-13 13" ${LINE} stroke-width="3.2"/>`),
  /** The weapon sheet: the backpack of the game's own interface (its help: "the backpack at the top right"). */
  weapons: svg(
    `<path d="M10 4.2V2.4h4v1.8" ${LINE} stroke-width="1.8"/><path d="M4.5 11C4.5 6 7.5 4 12 4s7.5 2 7.5 7v8.6a2.4 2.4 0 0 1-2.4 2.4H6.9a2.4 2.4 0 0 1-2.4-2.4z" ${FILL}/>` +
      `<path d="M4.8 11.2h14.4" stroke="rgba(0,0,0,.5)" stroke-width="1.4"/><rect x="8" y="14" width="8" height="5.2" rx="1.2" fill="rgba(0,0,0,.45)"/><path d="M2.5 12.5v6M21.5 12.5v6" ${LINE} stroke-width="1.8"/>`,
  ),
  fuse: (seconds: number) =>
    svg(`<circle cx="12" cy="13.5" r="9" ${LINE} stroke-width="1.8"/><path d="M9.5 2.2h5M12 2.2v2.3M19 5.5l1.6-1.6" ${LINE} stroke-width="1.8"/><text x="12" y="18" text-anchor="middle" font-family="Arial,sans-serif" font-weight="bold" font-size="12.5" ${FILL}>${seconds}</text>`),
  /** The bounce: a ball's dotted hops, high (max) or low (min). */
  bounce: (high: boolean) =>
    svg(`<path d="M1 22.8h22" ${LINE} stroke-width="1.6"/><path d="${high ? 'M1.8 22Q6 -2 10.2 22Q13.6 8 17 22' : 'M1.8 22Q6 13 10.2 22Q13.6 17 17 22'}" ${LINE} stroke-width="2" stroke-dasharray="2.2 2.2"/><circle cx="20" cy="19.2" r="3.4" ${FILL}/>`),
  /** A strike from the left (its projectiles fly to the right and down), or mirrored from the right. */
  strike: (fromRight: boolean) => svg(`<path d="M3 4 16.5 17.5" ${LINE} stroke-width="2.8"/><path d="M20.5 21.5 11 19.2 18.2 12z" ${FILL}/><path d="M2 9.5h5M6 2v5" ${LINE} stroke-width="1.6"/>`, fromRight),
  /** Turn (a girder's next shape, the next worm): a circular arrow, anticlockwise when mirrored. */
  turn: (anticlockwise: boolean) => svg(`<path d="M19.5 12.5A7.5 7.5 0 1 1 16.8 6.3" ${LINE} stroke-width="2.4"/><path d="M21 2.8 20.4 9.6 14.1 7z" ${FILL}/>`, anticlockwise),
  clock: svg(`<circle cx="12" cy="13" r="9" ${LINE} stroke-width="2"/><path d="M12 8v5l3 2M9.5 2h5" ${LINE} stroke-width="2"/>`),
} as const;

export class TouchControls {
  readonly root: HTMLElement;
  private readonly doc: Document;
  private readonly held = new Map<number, { keyCode: number; at: number; el: HTMLElement; until?: () => boolean } | { run: () => void; el: HTMLElement }>();
  private shown = false;
  private layoutKey = '';
  private readonly pad: HTMLElement;
  private readonly act: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly info: HTMLElement;
  private readonly marker: HTMLElement;
  private readonly buttons = new Map<Slot, HTMLButtonElement>();
  private readonly bindings = new Map<Slot, Binding | null>();
  private current: ControlsState | null = null;
  /** A target mode: a finger has chosen a point of the map (the game's cursor is there) since the mode began. */
  private armed = false;
  private mapPointer: number | null = null;
  private hintText = '';
  /**
   * The hint's place (layout): its bottom's middle over the stage's bottom edge (or its top under it; a room of its own
   * `y`: lifted over the d-pad's bottom row), and the top.
   */
  private hintAt: { rooms: Array<{ x: number; width: number; y?: number }>; y: number; below: boolean; top: number } | null = null;
  /** The room of hintAt.rooms the hint takes now (fitHint). */
  private hintRoom = 0;
  private hintPlaceKey = '';
  private infoKey = '';
  /** Where the menus' info goes (layout): beside the window (its width), or at the d-pad's corner. */
  private infoAt: { beside: boolean; width: number; left: number; bottom: number } | null = null;
  private infoPlaceKey = '';

  constructor(private readonly opts: TouchControlsOptions) {
    this.doc = opts.doc ?? document;
    const doc = this.doc;
    if (!doc.getElementById('fwp-touch-style')) {
      const style = doc.createElement('style');
      style.id = 'fwp-touch-style';
      style.textContent = CSS;
      doc.head.appendChild(style);
    }
    this.root = doc.createElement('div');
    this.root.id = 'fwp-touch';
    this.pad = doc.createElement('div');
    this.pad.className = 'cluster';
    this.act = doc.createElement('div');
    this.act.className = 'cluster';
    this.hint = doc.createElement('div');
    this.hint.className = 'hint';
    this.hint.setAttribute('role', 'status');
    this.info = doc.createElement('div');
    this.info.className = 'info';
    this.marker = doc.createElement('div');
    this.marker.className = 'marker';
    this.root.append(this.marker, this.pad, this.act, this.hint, this.info);
    doc.body.appendChild(this.root);
    this.root.addEventListener('contextmenu', (e) => e.preventDefault());
    // A target mode's point: a finger that lifts from the battle's map (the game's cursor follows it there).
    const win = doc.defaultView as Window | null;
    win?.addEventListener(
      'pointerdown',
      (e: PointerEvent) => {
        if (this.current?.mode !== 'target' || this.root.contains(e.target as Node)) return;
        this.mapPointer = this.opts.onMap?.(e.clientX, e.clientY) ? e.pointerId : null;
      },
      true,
    );
    win?.addEventListener(
      'pointerup',
      (e: PointerEvent) => {
        if (e.pointerId !== this.mapPointer) return;
        this.mapPointer = null;
        if (this.current?.mode === 'target') this.armed = true;
      },
      true,
    );
  }

  private keyboard = false;

  /**
   * A physical keyboard is in use (a key pressed outside text fields): the controls step aside until the next touch
   * (a touch laptop, a phone with a keyboard attached).
   */
  setKeyboardInUse(on: boolean): void {
    this.keyboard = on;
  }

  /** Shows or hides the controls for the game's current state, places and fits them; called every frame. */
  update(): void {
    // (the buttons shown may change below: the game asks for them again)
    this.shownCache = null;
    const state = this.keyboard ? null : this.opts.state();
    const want = !!state;
    if (want !== this.shown) {
      this.shown = want;
      this.root.style.display = want ? 'block' : 'none';
      if (!want) this.releaseAll();
    }
    if (!state) {
      this.current = null;
      this.armed = false;
      return;
    }
    this.layout();
    this.apply(state);
  }

  /** The battle's buttons are shown (the local player controls a worm, no menu is open). */
  get visible(): boolean {
    return this.shown && !!this.current && this.current.mode !== 'menu';
  }

  /**
   * Whether client point (x, y) is within `reach` CSS px of a shown button (a finger that missed it): the game's touch
   * slop keeps off there, so a near miss never presses a HUD button beside the controls (src/main.ts).
   */
  near(x: number, y: number, reach: number): boolean {
    if (!this.visible) return false;
    for (const el of this.buttons.values()) {
      if (el.style.display === 'none') continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left - reach && x <= r.right + reach && y >= r.top - reach && y <= r.bottom + reach) return true;
    }
    return false;
  }

  /** The game's state the controls show now (null: hidden). */
  get state(): ControlsState | null {
    return this.shown ? this.current : null;
  }

  /** A target mode has its point on the map (the fire button confirms it). */
  get targetSet(): boolean {
    return this.armed;
  }

  /** Releases every held key (the controls hide, the app pauses). */
  releaseAll(): void {
    for (const [id, h] of [...this.held]) {
      this.held.delete(id);
      h.el.classList.remove('on');
      if ('keyCode' in h) this.opts.key('up', h.keyCode, 0);
    }
  }

  destroy(): void {
    this.releaseAll();
    this.root.remove();
  }

  // ---------------------------------------------------------------- placement

  private layout(): void {
    const win = this.doc.defaultView as Window;
    const W = win.innerWidth;
    const H = win.innerHeight;
    const s = this.opts.stageRect() ?? { left: 0, top: 0, width: W, height: H };
    const safe = this.opts.safeInsets?.() ?? NO_INSETS;
    const clear = this.opts.keepClear?.() ?? [];
    const key =
      `${W}x${H}|${Math.round(s.left)},${Math.round(s.top)},${Math.round(s.width)},${Math.round(s.height)}|${safe.left},${safe.top},${safe.right},${safe.bottom}|` +
      clear.map((r) => `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)},${Math.round(r.height)}`).join(';');
    if (key === this.layoutKey) return;
    this.layoutKey = key;
    const p = placeControls(W, H, s, safe, clear);
    const { mode, padSize, padH } = p;
    this.place(this.pad, p.pad.x, p.pad.y);
    this.place(this.act, p.act.x, p.act.y);
    // More transparent over a tablet's window only: a phone's buttons lie over the battle's map wherever they are.
    this.root.classList.toggle('over', mode === 'over' && Math.min(W, H) >= TABLET_MIN_PX);
    // The hint: over the stage's bottom edge, between the clusters (under the stage in portrait); over the window's
    // corners (a narrow phone, a tablet), between the clusters' inner edges in two lines at most, or else (a small
    // phone) one row higher, over the d-pad's bottom row: from right of its ▲ (fitHint).
    const room = (l: number, r: number, y?: number) => ({ x: Math.round((l + r) / 2), width: Math.max(160, Math.min(520, Math.round(r - l))), ...(y === undefined ? {} : { y }) });
    const rooms =
      mode === 'over'
        ? [room(p.pad.x + padSize + MIN_GAP_PX, p.act.x - MIN_GAP_PX), room(p.pad.x + 2 * p.unit + p.gap + MIN_GAP_PX, p.act.x - MIN_GAP_PX, Math.round(p.pad.y + p.unit + p.gap - MIN_GAP_PX))]
        : [room(s.left + 12, s.left + s.width - 12)];
    this.hintAt = { rooms, y: Math.round(mode === 'bottom' ? s.top + s.height + 8 : Math.min(s.top + s.height, H) - 8), below: mode === 'bottom', top: Math.round(Math.max(s.top, safe.top) + 8) };
    this.hintPlaceKey = '';
    this.hintText = ''; // (measured again in its new room)
    // The menus' info: at the top left in the room left of the window when there is some (8 px from it: the weapon
    // sheet fills the window); else where the d-pad is, which a menu hides: over the lower left corner of the sheet,
    // clear of its header and its first rows of weapons (a narrow phone, a tablet), or under the window (portrait).
    const leftBar = s.left - safe.left;
    this.infoAt = { beside: leftBar - 16 >= 120, width: Math.round(leftBar - 16), left: Math.round(p.pad.x), bottom: Math.round(H - (p.pad.y + padH)) };
    this.build(p.unit, p.gap);
  }

  private place(el: HTMLElement, x: number, y: number): void {
    el.style.left = `${Math.round(x)}px`;
    el.style.top = `${Math.round(y)}px`;
  }

  private builtUnit = 0;

  /** The buttons at their places in the two clusters (the d-pad; fire, jumps, weapon, fuse and bounce). */
  private build(unit: number, gap: number): void {
    if (this.builtUnit === unit) return;
    this.builtUnit = unit;
    this.releaseAll();
    this.pad.replaceChildren();
    this.act.replaceChildren();
    this.buttons.clear();
    this.bindings.clear();
    const at = (col: number, row: number) => ({ x: col * (unit + gap), y: row * (unit + gap) });
    // ▲ over ▼; ◀ ▼ ▶ in one row along the bottom (placeControls: padH).
    const pad: Array<[Slot, number, { x: number; y: number }]> = [
      ['up', unit, at(1, 0)],
      ['left', unit, at(0, 1)],
      ['down', unit, at(1, 1)],
      ['right', unit, at(2, 1)],
    ];
    for (const [slot, size, p] of pad) this.pad.appendChild(this.button(slot, size, p.x, p.y));
    const fire = Math.round(unit * 1.35);
    const small = Math.max(MIN_BUTTON_PX, Math.round(unit * 0.85));
    const right = fire + gap * 2 + unit;
    const act: Array<[Slot, number, number, number]> = [
      ['weapon', small, right - small, 0],
      ['fuse', small, right - small * 2 - gap, 0],
      // Above the fuse: both appear with the weapons that have them (grenades), and nothing else is there.
      ['bounce', small, right - small * 2 - gap, -(small + gap)],
      ['jump', unit, fire + gap * 2, small + gap],
      ['backflip', unit, fire + gap * 2, small + unit + gap * 2],
      ['fire', fire, 0, unit * 3 + gap * 2 - fire],
    ];
    for (const [slot, size, x, y] of act) this.act.appendChild(this.button(slot, size, x, y));
  }

  // ---------------------------------------------------------------- what the buttons do

  /** Each button's binding for the game's state: shown or hidden, its icon, its key or action. */
  private bindingsFor(s: ControlsState): Record<Slot, Binding | null> {
    const k = (code: number, char = 0) => () => [code, char] as [number, number];
    const b: Record<Slot, Binding | null> = { up: null, left: null, right: null, down: null, fire: null, jump: null, backflip: null, weapon: null, fuse: null, bounce: null };
    if (s.mode === 'menu') {
      b.weapon = { act: 'close', icon: ICON.cancel, run: () => this.opts.back?.() };
      return b;
    }
    if (s.sides === 'walk' || s.sides === 'steer') {
      b.left = { act: s.sides + '-left', icon: ICON.left, key: k(KEY.left) };
      b.right = { act: s.sides + '-right', icon: ICON.right, key: k(KEY.right) };
    } else if (s.sides === 'strike') {
      b.left = { act: 'strike-from-left', icon: ICON.strike(false), key: k(KEY.left), sel: s.strikeFrom === 'left' };
      b.right = { act: 'strike-from-right', icon: ICON.strike(true), key: k(KEY.right), sel: s.strikeFrom === 'right' };
    } else if (s.sides === 'girder') {
      // proc.check_mousecontrol: left (or down) takes the girder's next shape, right (or up) the previous one.
      b.left = { act: 'girder-next', icon: ICON.turn(true), key: k(KEY.left) };
      b.right = { act: 'girder-previous', icon: ICON.turn(false), key: k(KEY.right) };
    } else if (s.sides === 'worm') {
      b.right = { act: 'next-worm', icon: ICON.turn(false), key: k(KEY.enter, 13) };
    }
    if (s.vertical) {
      b.up = { act: s.vertical + '-up', icon: ICON.up, key: k(KEY.up) };
      b.down = { act: s.vertical + '-down', icon: ICON.down, key: k(KEY.down) };
    }
    if (s.jumps) {
      b.jump = { act: 'jump-' + s.facing, icon: ICON.jump(s.facing === 'left'), key: k(KEY.enter, 13) };
      b.backflip = { act: 'backflip-' + (s.facing === 'left' ? 'right' : 'left'), icon: ICON.backflip(s.facing === 'left'), key: k(KEY.backspace, 8) };
    }
    if (s.mode === 'target') {
      b.fire = { act: 'confirm-target', icon: ICON.target, run: () => this.confirmTarget(), idle: !this.armed };
    } else if (s.mode === 'worm') {
      b.fire = { act: 'choose-worm', icon: ICON.check, key: k(KEY.escape, 27) };
    } else if (s.fire) {
      const icon = s.fire === 'stop' ? ICON.stop : s.fire === 'detonate' ? ICON.detonate : s.fire === 'use' ? ICON.check : ICON.fire;
      b.fire = { act: s.fire, icon, key: k(KEY.space, 32) };
    }
    if (s.cancel) b.weapon = { act: 'cancel', icon: ICON.cancel, key: k(KEY.escape, 27) };
    else if (s.weapons) b.weapon = { act: 'weapons', icon: ICON.weapons, key: k(KEY.q, 113), until: () => this.current?.mode !== 'turn' };
    if (s.fuse !== null) {
      // The next fuse (1..5, then 1 again), as the game's digit keys set it.
      const next = (s.fuse % 5) + 1;
      b.fuse = { act: 'fuse-' + s.fuse, icon: ICON.fuse(s.fuse), key: k(48 + next, 48 + next) };
    }
    if (s.bounce) {
      b.bounce = s.bounce === 'max' ? { act: 'bounce-max', icon: ICON.bounce(true), key: k(KEY.minus, 45) } : { act: 'bounce-min', icon: ICON.bounce(false), key: k(KEY.plus, 61) };
    }
    return b;
  }

  /** Shows the buttons, the hint and the info of the game's state (touching the page only where something changed). */
  private apply(s: ControlsState): void {
    const prev = this.current;
    if (s.mode !== 'target' || prev?.mode !== 'target' || prev.target !== s.target) {
      this.armed = false;
      if (s.mode !== 'target') this.mapPointer = null;
    }
    this.current = s;
    const bindings = this.bindingsFor(s);
    for (const [slot, el] of this.buttons) {
      const next = bindings[slot];
      const before = this.bindings.get(slot) ?? null;
      this.bindings.set(slot, next);
      if (!next) {
        if (before) {
          el.style.display = 'none';
          this.releaseButton(el);
        }
        continue;
      }
      if (!before) el.style.display = '';
      if (!before || before.icon !== next.icon) el.innerHTML = next.icon;
      if (!before || before.act !== next.act) {
        el.dataset.act = next.act;
        el.setAttribute('aria-label', next.act);
      }
      el.classList.toggle('idle', !!next.idle);
      el.classList.toggle('sel', !!next.sel);
    }
    // The chosen point of a target mode: a ring around the game's own cursor.
    const at = s.mode === 'target' && this.armed ? this.opts.pointer?.() : null;
    this.marker.style.display = at ? 'block' : 'none';
    if (at) {
      this.marker.style.left = `${Math.round(at.clientX)}px`;
      this.marker.style.top = `${Math.round(at.clientY)}px`;
    }
    const t = (x: string) => (this.opts.text ? this.opts.text(x) : x);
    const ready = s.mode === 'target' && this.armed;
    const lines = s.mode === 'menu' ? [] : ready ? s.hintReady : s.hint;
    // (the icons a line names are the buttons' icons now)
    const icons: Record<string, string> = { left: bindings.left?.icon ?? '', right: bindings.right?.icon ?? '', fire: bindings.fire?.icon ?? '' };
    const text = lines.map(t).join('\n');
    const key = text + '|' + (/\{(left|right|fire)\}/.test(text) ? `${icons.left}${icons.right}${icons.fire}` : '');
    if (key !== this.hintText) {
      this.hintText = key;
      this.hint.replaceChildren(...lines.map((l) => this.hintLine(t(l), icons)));
      this.hint.style.display = text ? 'block' : 'none';
      this.fitHint();
    }
    this.hint.classList.toggle('ready', ready);
    this.placeHint(ready ? at ?? null : null);
    const infoLines = s.mode === 'menu' ? s.hint.map(t) : [];
    const infoKey = `${s.mode === 'menu' ? s.clock : ''}|${infoLines.join('\n')}`;
    if (infoKey !== this.infoKey) {
      this.infoKey = infoKey;
      const parts: HTMLElement[] = [];
      if (s.mode === 'menu' && s.clock !== null) {
        const clock = this.doc.createElement('div');
        clock.className = 'clock' + (s.clock <= 5 ? ' low' : '');
        clock.innerHTML = ICON.clock;
        clock.append(String(s.clock));
        parts.push(clock);
      }
      for (const l of infoLines) parts.push(Object.assign(this.doc.createElement('div'), { textContent: l }));
      this.info.replaceChildren(...parts);
      this.info.style.display = parts.length ? 'block' : 'none';
    }
    this.placeInfo();
  }

  /** One line of the hint: its text, `{left}` / `{right}` / `{fire}` drawn as those buttons' icons. */
  private hintLine(text: string, icons: Record<string, string>): HTMLElement {
    const line = this.doc.createElement('div');
    for (const part of text.split(/(\{(?:left|right|fire)\})/)) {
      const icon = /^\{(left|right|fire)\}$/.exec(part);
      if (!icon) {
        if (part) line.append(part);
        continue;
      }
      const el = this.doc.createElement('span');
      el.className = 'ico';
      el.innerHTML = icons[icon[1]];
      line.append(el);
    }
    return line;
  }

  /**
   * The hint's room and font (layout's hintAt.rooms): the first room it takes in two lines at most, at 14 px or else
   * 13 px; in the last room with 13 px when it needs more lines.
   */
  private fitHint(): void {
    const a = this.hintAt;
    this.hintPlaceKey = '';
    if (!a || this.hint.style.display === 'none') return;
    const lines = () => {
      const cs = getComputedStyle(this.hint);
      const h = this.hint.getBoundingClientRect().height - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      return Math.round(h / (parseFloat(cs.fontSize) * 1.3));
    };
    for (let i = 0; i < a.rooms.length; i++) {
      this.hintRoom = i;
      this.hint.style.maxWidth = `${a.rooms[i].width}px`;
      this.hint.classList.remove('small');
      if (lines() <= 2) return;
      this.hint.classList.add('small');
      if (lines() <= 2 || i === a.rooms.length - 1) return;
    }
  }

  /**
   * Places the hint (layout's hintAt): over the stage's bottom edge between the clusters, or, when it would cover the
   * target chosen (`target`: its client point), at the top under the team bars (hintTop), clear of it.
   */
  private placeHint(target: { clientX: number; clientY: number } | null): void {
    const a = this.hintAt;
    if (!a || this.hint.style.display === 'none') return;
    const room = a.rooms[Math.min(this.hintRoom, a.rooms.length - 1)];
    const x = room.x;
    const at = room.y ?? a.y;
    let top = false;
    if (target) {
      const r = this.hint.getBoundingClientRect();
      const x0 = x - r.width / 2;
      const y0 = a.below ? at : at - r.height;
      const reach = 34; // the ring round the target (44 px, pulsing to 52) and a finger's width
      top = !a.below && target.clientX > x0 - reach && target.clientX < x0 + r.width + reach && target.clientY > y0 - reach && target.clientY < y0 + r.height + reach;
    }
    const y = top ? Math.max(a.top, Math.round(this.opts.hintTop?.() ?? a.top)) : at;
    const key = `${x},${y},${top},${a.below}`;
    if (key === this.hintPlaceKey) return;
    this.hintPlaceKey = key;
    this.hint.style.left = `${x}px`;
    this.hint.style.top = `${y}px`;
    this.hint.classList.toggle('below', a.below && !top);
    this.hint.classList.toggle('top', top);
  }

  /**
   * The battle's buttons shown now (their slots on the page, CSS px); none while hidden or a menu is open. They change
   * only in update(): the game's frames ask for them many times between two updates, which get the same list.
   */
  shownRects(): CssRect[] {
    if (this.shownCache) return this.shownCache;
    const out: CssRect[] = [];
    if (this.visible) {
      for (const [slot, el] of this.buttons) {
        if (el.style.display === 'none' || !this.bindings.get(slot)) continue;
        const r = this.slotRect(slot);
        if (r) out.push(r);
      }
    }
    this.shownCache = out;
    return out;
  }

  private shownCache: CssRect[] | null = null;

  /** Where a button's slot is on the page (CSS px), shown or not; null before the controls were first laid out. */
  slotRect(slot: Slot): CssRect | null {
    const b = this.buttons.get(slot);
    const c = b?.parentElement as HTMLElement | null | undefined;
    if (!b || !c || !c.style.left) return null;
    return { left: parseFloat(c.style.left) + parseFloat(b.style.left), top: parseFloat(c.style.top) + parseFloat(b.style.top), width: parseFloat(b.style.width), height: parseFloat(b.style.height) };
  }

  /** The game's target cursor waits for a finger's first point on the map (a target mode before its point is chosen). */
  get targetPending(): boolean {
    return this.shown && this.current?.mode === 'target' && !this.armed;
  }

  /**
   * Places the menus' info (layout's infoAt): at the d-pad's corner it narrows to the room left of the menu's content
   * it would cover (a long list of weapons reaching the sheet's bottom), not below INFO_MIN_PX.
   */
  private placeInfo(): void {
    const a = this.infoAt;
    if (!a || !this.info.firstChild) return;
    // The place the open menu keeps for it (the weapon sheet's footer row on a phone).
    const at = this.opts.menuInfoAt?.() ?? null;
    if (at) {
      const key = `slot|${Math.round(at.left)},${Math.round(at.top)},${Math.round(at.width)},${Math.round(at.height)}|${at.hidden}|${this.infoKey}`;
      if (key === this.infoPlaceKey) return;
      this.infoPlaceKey = key;
      this.info.classList.remove('corner');
      this.info.classList.add('slot');
      // (hidden while the menu covers that place: a weapon's description over the cells of a small phone)
      this.info.style.display = at.hidden ? 'none' : 'flex';
      this.info.style.left = `${Math.round(at.left)}px`;
      this.info.style.top = `${Math.round(at.top + at.height / 2)}px`;
      this.info.style.bottom = '';
      this.info.style.maxWidth = `${Math.round(at.width)}px`;
      return;
    }
    const clear = a.beside ? [] : (this.opts.menuKeepClear?.() ?? []);
    const key = `${a.beside ? a.width : `${a.left},${a.bottom}`}|${this.infoKey}|${clear.map((r) => `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)},${Math.round(r.height)}`).join(';')}`;
    if (key === this.infoPlaceKey) return;
    this.infoPlaceKey = key;
    if (this.info.classList.contains('slot')) {
      this.info.classList.remove('slot');
      this.info.style.display = 'block';
      this.info.style.top = '';
    }
    this.info.classList.toggle('corner', !a.beside);
    this.info.style.left = a.beside ? '' : `${a.left}px`;
    this.info.style.bottom = a.beside ? '' : `${a.bottom}px`;
    this.info.style.maxWidth = a.beside ? `${a.width}px` : '';
    if (!clear.length) return;
    const r = this.info.getBoundingClientRect();
    const hit = clear.filter((c) => r.left < c.left + c.width && c.left < r.right && r.top < c.top + c.height && c.top < r.bottom);
    if (!hit.length) return;
    const room = Math.min(...hit.map((c) => c.left)) - MIN_GAP_PX - r.left;
    this.info.style.maxWidth = `${Math.max(INFO_MIN_PX, Math.round(room))}px`;
  }

  /**
   * The fire button of a target mode: the double click of the mouse at the game's cursor (where the finger chose the
   * point), as the original confirms a strike, a teleport or a girder; before a point is chosen, the hint again.
   */
  private confirmTarget(): void {
    const at = this.armed ? this.opts.pointer?.() : null;
    if (!at || !this.opts.mouse) {
      this.hint.classList.remove('nudge');
      void this.hint.offsetWidth; // restart the animation
      this.hint.classList.add('nudge');
      return;
    }
    this.armed = false;
    for (const type of ['down', 'up', 'down', 'up'] as const) this.opts.mouse(type, at.x, at.y);
  }

  private releaseButton(el: HTMLElement): void {
    for (const [id, h] of [...this.held]) {
      if (h.el !== el) continue;
      this.held.delete(id);
      el.classList.remove('on');
      if ('keyCode' in h) this.opts.key('up', h.keyCode, 0);
    }
  }

  private button(slot: Slot, size: number, x: number, y: number): HTMLButtonElement {
    const b = this.doc.createElement('button');
    b.type = 'button';
    b.tabIndex = -1;
    b.dataset.key = slot;
    b.style.display = 'none';
    Object.assign(b.style, { left: `${x}px`, top: `${y}px`, width: `${size}px`, height: `${size}px` });
    this.buttons.set(slot, b);
    b.addEventListener('pointerdown', (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.held.has(e.pointerId)) return;
      const binding = this.bindings.get(slot);
      if (!binding) return;
      try {
        b.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      b.classList.add('on');
      try {
        this.opts.vibrate?.(HAPTIC_MS);
      } catch {
        /* ignore */
      }
      if (binding.run) {
        this.held.set(e.pointerId, { run: binding.run, el: b });
        return;
      }
      const [keyCode, charCode] = binding.key!();
      this.held.set(e.pointerId, { keyCode, at: Date.now(), el: b, until: binding.until });
      this.opts.key('down', keyCode, charCode);
    });
    const release = (e: PointerEvent) => {
      const h = this.held.get(e.pointerId);
      if (!h || h.el !== b) return;
      this.held.delete(e.pointerId);
      if (!('keyCode' in h)) {
        b.classList.remove('on');
        // An action happens on a release over the button (a finger slid off cancels it).
        const r = b.getBoundingClientRect();
        if (e.type === 'pointerup' && e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) h.run();
        return;
      }
      const up = () => {
        const held = Date.now() - h.at;
        if (held < MIN_PRESS_MS || (h.until && !h.until() && held < STICKY_PRESS_MS)) {
          setTimeout(up, held < MIN_PRESS_MS ? MIN_PRESS_MS - held : 20);
          return;
        }
        b.classList.remove('on');
        this.opts.key('up', h.keyCode, 0);
      };
      up();
    };
    b.addEventListener('pointerup', release);
    b.addEventListener('pointercancel', release);
    b.addEventListener('lostpointercapture', release);
    return b;
  }
}

/**
 * A touch screen as the main input: the primary pointer is coarse (a phone, a tablet). Touch points alone
 * (navigator.maxTouchPoints) do not count: a laptop with a touch screen that is driven by its mouse and keyboard
 * stays a desktop (no on-screen controls, no pause in the background) until it is really touched (watchFirstTouch).
 */
export function isTouchScreen(win: any = globalThis): boolean {
  try {
    return !!win.matchMedia?.('(pointer: coarse)')?.matches;
  } catch {
    return false;
  }
}

/**
 * Calls `onTouch` once, at the first real touch of the page (a pointerdown with pointerType 'touch'; a mouse or a pen
 * does not count). Returns a function that stops watching.
 */
export function watchFirstTouch(onTouch: () => void, win: any = globalThis): () => void {
  const listener = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') return;
    stop();
    onTouch();
  };
  const stop = (): void => win.removeEventListener?.('pointerdown', listener, { capture: true });
  win.addEventListener?.('pointerdown', listener, { capture: true });
  return stop;
}
